// This file is auto-generated by @hey-api/openapi-ts

import * as z from 'zod';

/**
 * Often a UUID. But there are exceptions. Always handle an ID as a string.
 */
export const zIdString = z.string();

/**
 * An ISO 8601 formatted timestamp.
 */
export const zTimestamp = z.string();

/**
 * Data pertaining to the loading of a transport order
 */
export const zLoading = z.object({
    note: z.string().optional(),
});

/**
 * Data pertaining to the unloading of a transport order
 */
export const zUnloading = z.object({
    note: z.string().optional(),
});

export const zGoodsBase = z.object({
    type: z.string(),
});

export const zMaterial = z.object({
    id: z.string(),
    name: z.string(),
});

export const zMaterialQuantity = z.object({
    type: z.string(),
    value: z.number(),
});

export const zCustomerTrailerProofOfHandling = z
    .object({
        confirmed_at: zTimestamp,
        license_plate: z.string().optional(),
    })
    .readonly();

/**
 * A trailer that can be assigned as goods for a transport order.
 */
export const zTrailerUnit = z
    .object({
        id: z.string(),
        license_plate: z.string().optional(),
        note: z.string().optional(),
        proof_of_loading: zCustomerTrailerProofOfHandling.optional(),
        proof_of_delivery: zCustomerTrailerProofOfHandling.optional(),
    })
    .readonly();

/**
 * The trailers that are to be delivered in the transport order.
 */
export const zCustomerTrailer = zGoodsBase.and(
    z.object({
        type: z.enum(['customer-trailer']).optional(),
        trailers: z.array(zTrailerUnit),
    })
);

export const zSwapBodyProofOfHandling = z
    .object({
        confirmed_at: zTimestamp,
        swap_body_reference: z.string().optional(),
    })
    .readonly();

export const zSwapBodyUnit = z
    .object({
        id: z.string(),
        proof_of_loading: zSwapBodyProofOfHandling.optional(),
        proof_of_delivery: zSwapBodyProofOfHandling.optional(),
    })
    .readonly();

/**
 * The swap body used in a transport order.
 */
export const zSwapBody = zGoodsBase.and(
    z.object({
        type: z.enum(['swap-body']).optional(),
        units: z.array(zSwapBodyUnit),
    })
);

export const zDimension = z.object({
    length_cm: z.number(),
    width_cm: z.number(),
    height_cm: z.number(),
});

export const zWeight = z.object({
    weight_kg: z.number(),
});

export const zLoadingMeter = z.object({
    value: z.number(),
});

export const zArticleQuantityInteger = z.object({
    type: z.string(),
    value: z
        .int()
        .min(-2147483648, { error: 'Invalid value: Expected int32 to be >= -2147483648' })
        .max(2147483647, { error: 'Invalid value: Expected int32 to be <= 2147483647' }),
});

export const zArticleQuantityDecimal = z.object({
    type: z.string(),
    value: z.number(),
});

export const zArticleQuantity = z.union([zArticleQuantityInteger, zArticleQuantityDecimal]);

export const zArticle = z.object({
    customer_article_id: z.string().optional(),
    quantity: zArticleQuantity.optional(),
    name: z.string().optional(),
    article_id: z.string().optional(),
});

/**
 * The current status of the tour.
 */
export const zTourStatus = z.string();

/**
 * A dictionary containing additional customer data for the resource.
 */
export const zAdditionalCustomerData = z.record(z.string(), z.string());

export const zAssetId = z.object({
    asset_id: zIdString.optional(),
});

export const zLicensePlate = z.object({
    license_plate: z
        .object({
            plate_number: z.string(),
            country_code: z.string(),
        })
        .optional(),
});

/**
 * The means of transport used for a tour. A tour without a set `MeansOfTransport` cannot be started.
 */
export const zMeansOfTransport = zAssetId.and(zLicensePlate);

/**
 * A trailer that can be coupled with the main means of transport for this tour.
 */
export const zTrailer = zAssetId.and(zLicensePlate);

export const zOrderHandle = zIdString;

/**
 * A time window defined by the two time points `start_at` and `end_at`.
 */
export const zTimeWindow = z.object({
    start_at: zTimestamp,
    end_at: zTimestamp,
});

/**
 * The address of a location.
 */
export const zAddress = z.object({
    street: z.string().optional(),
    postal_code: z.string().optional(),
    city: z.string().optional(),
    country: z.string().optional(),
});

/**
 * Geographic coordinates consisting of a `latitude` and a `longitude`.
 */
export const zCoordinates = z.object({
    latitude: z.number(),
    longitude: z.number(),
});

export const zEventSignatureCapture = z.object({
    document_id: zIdString,
    file_name: z.string().optional(),
    taken_at: zTimestamp,
    taken_at_position: zCoordinates.optional(),
});

/**
 * A physical location specified by an `Address` and geographic `Coordinates`.
 */
export const zLocation = z.object({
    name: z.string().optional(),
    address: zAddress.optional(),
    coordinates: zCoordinates,
    external_id: z.string().optional(),
});

export const zMileageState = z.object({
    mileage: z.number(),
    occurred_at: zTimestamp.optional(),
});

/**
 * Contact information of e.g. the shipment consignee
 */
export const zContact = z.object({
    name: z.string().optional(),
    email: z.string().optional(),
    phone: z.string().optional(),
    language: z.string().optional(),
    has_agreed_to_receive_emails: z.boolean().optional(),
});

export const zTourProblemType = z.string();

export const zChangeType = z.enum(['added', 'removed', 'updated', 'reordered']);

export const zFieldChange = z.object({
    field: z.string(),
    before: z.unknown().optional(),
    after: z.unknown().optional(),
});

export const zTourStopChange = z.object({
    change_type: zChangeType,
    stop_id: z.string(),
    position_before: z.int().nullish(),
    position_after: z.int().nullish(),
    field_changes: z.array(zFieldChange).optional(),
});

export const zTransportOrderChange = z.object({
    change_type: zChangeType,
    transport_order_id: z.string(),
    load_stop_id_before: z.string().nullish(),
    load_stop_id_after: z.string().nullish(),
    unload_stop_id_before: z.string().nullish(),
    unload_stop_id_after: z.string().nullish(),
    field_changes: z.array(zFieldChange).optional(),
});

export const zServiceOrderChange = z.object({
    change_type: zChangeType,
    service_order_id: z.string(),
    field_changes: z.array(zFieldChange).optional(),
});

export const zTourChangeSet = z.object({
    tour_fields: z.array(zFieldChange).optional(),
    stops: z.array(zTourStopChange).optional(),
    transport_orders: z.array(zTransportOrderChange).optional(),
    service_orders: z.array(zServiceOrderChange).optional(),
});

export const zTourEvent = z.object({
    id: z.uuid(),
    account_id: z.uuid(),
    tour_id: z.string(),
    occurred_at: z.string(),
    event_type: z.enum([
        'tour-created',
        'tour-updated',
        'tour-deleted',
        'tour-acknowledged',
        'tour-started',
        'tour-finished',
    ]),
    triggered_at: z.string().optional(),
    triggered_at_position: zCoordinates.optional(),
    mileage_state: zMileageState.optional(),
    changes: zTourChangeSet.optional(),
});

export const zProblemAcknowledgedEvent = z.object({
    id: z.uuid(),
    account_id: z.uuid(),
    tour_id: z.string(),
    occurred_at: z.string(),
    event_type: z.enum(['problem-acknowledged']),
    problem_id: z.string(),
});

export const zCustomerTrailerConfirmedEvent = z.object({
    id: z.uuid(),
    account_id: z.uuid(),
    tour_id: z.string(),
    occurred_at: z.string(),
    event_type: z.enum(['trailer-confirmed']),
    tour_stop_id: z.string(),
    transport_order_id: z.string(),
    customer_trailer_id: z.string(),
    customer_trailer_license_plate: z.string().optional(),
    triggered_at_position: zCoordinates.optional(),
    mileage_state: zMileageState.optional(),
});

export const zSwapBodyConfirmedEvent = z.object({
    id: z.uuid(),
    account_id: z.uuid(),
    tour_id: z.string(),
    occurred_at: z.string(),
    event_type: z.enum(['swap-body-confirmed']),
    tour_stop_id: z.string(),
    transport_order_id: z.string(),
    swap_body_id: z.string(),
    swap_body_reference: z.string().optional(),
    triggered_at_position: zCoordinates.optional(),
    mileage_state: zMileageState.optional(),
});

export const zHandlingUnitEvent = z.object({
    id: z.uuid(),
    account_id: z.uuid(),
    tour_id: z.string(),
    occurred_at: z.string(),
    event_type: z.enum(['handling-unit-confirmed', 'additional-handling-unit-reported']),
    tour_stop_id: z.string(),
    transport_order_id: z.string(),
    handling_unit_id: z.string(),
    triggered_at: z.string().optional(),
    triggered_at_position: zCoordinates.optional(),
    mileage_state: zMileageState.optional(),
});

export const zBulkMaterialUnitConfirmedEvent = z.object({
    id: z.uuid(),
    account_id: z.uuid(),
    tour_id: z.string(),
    occurred_at: z.string(),
    event_type: z.enum(['bulk-material-unit-confirmed']),
    tour_stop_id: z.string(),
    transport_order_id: z.string(),
    triggered_at: z.string().optional(),
    triggered_at_position: zCoordinates.optional(),
    mileage_state: zMileageState.optional(),
});

export const zTransportOrderConfirmedEvent = z.object({
    id: z.uuid(),
    account_id: z.uuid(),
    tour_id: z.string(),
    occurred_at: z.string(),
    event_type: z.enum(['transport-order-confirmed']),
    tour_stop_id: z.string(),
    transport_order_id: z.string(),
    confirmation_type: z.enum(['loading', 'unloading']),
    driver_name: z.string().optional(),
    customer_name: z.string().optional(),
    customer_email: z.string().optional(),
    triggered_at: z.string(),
    triggered_at_position: zCoordinates.optional(),
    mileage_state: zMileageState.optional(),
    driver_signature: zEventSignatureCapture.optional(),
    customer_signature: zEventSignatureCapture.optional(),
});

export const zTourStopEvent = z.object({
    id: z.uuid(),
    account_id: z.uuid(),
    tour_id: z.string(),
    occurred_at: z.string(),
    event_type: z.enum([
        'tour-stop-reached',
        'loading-started',
        'loading-finished',
        'unloading-started',
        'unloading-finished',
        'execution-started',
        'execution-finished',
        'tour-stop-finished',
        'tour-stop-skipped',
    ]),
    tour_stop_id: z.string(),
    triggered_at_position: zCoordinates.optional(),
    mileage_state: zMileageState.optional(),
});

export const zTourProblemConfirmation = z.object({
    type: z.enum(['acknowledge-tour-problem']),
});

export const zHandlingUnitDeviation = z.object({
    quantity: z
        .int()
        .min(-2147483648, { error: 'Invalid value: Expected int32 to be >= -2147483648' })
        .max(2147483647, { error: 'Invalid value: Expected int32 to be <= 2147483647' })
        .optional(),
    weight: zWeight.optional(),
    loading_meter: zLoadingMeter.optional(),
    articles: z.array(zArticle).optional(),
    comment: z.string().optional(),
});

export const zProofOfHandling = z.object({
    confirmed_at: zTimestamp,
    deviation: zHandlingUnitDeviation.optional(),
});

export const zBulkMaterialUnitDeviation = z.object({
    quantity: zMaterialQuantity.optional(),
    comment: z.string().optional(),
});

export const zBulkMaterialUnitProofOfHandling = z.object({
    confirmed_at: zTimestamp,
    deviation: zBulkMaterialUnitDeviation.optional(),
});

/**
 * A document pertaining to (some part of) this `Tour`
 */
export const zWriteDocument = z.object({
    id: zIdString,
    type: z.string(),
    name: z.string(),
    file_name: z.string(),
    external_id: z.string().optional(),
});

export const zWriteDocuments = z.array(zWriteDocument);

export const zTourProblem = z
    .object({
        id: zIdString,
        type: zTourProblemType,
        tour_stop_id: zIdString.optional(),
        documents: zWriteDocuments.optional(),
        reported_at: zTimestamp.optional(),
        acknowledged_at: zTimestamp.optional(),
    })
    .readonly();

export const zTourProblems = z.array(zTourProblem);

export const zProblemReportedEvent = z.object({
    id: z.uuid(),
    account_id: z.uuid(),
    tour_id: z.string(),
    occurred_at: z.string(),
    event_type: z.enum(['problem-reported']),
    problem: zTourProblem,
    triggered_at_position: zCoordinates.optional(),
    mileage_state: zMileageState.optional(),
});

export const zBaseTourEvent = z.union([
    zTourEvent,
    zTourStopEvent,
    zProblemReportedEvent,
    zProblemAcknowledgedEvent,
    zCustomerTrailerConfirmedEvent,
    zSwapBodyConfirmedEvent,
    zHandlingUnitEvent,
    zBulkMaterialUnitConfirmedEvent,
    zTransportOrderConfirmedEvent,
]);

/**
 * An association of the document with a `Tour`, `Tour Stop`, `Service Order`, `Transport Order`, `Problem`, or `Handling Unit`.
 * `id` identifies the entity this document is associated with, e.g. tour stop id.
 *
 */
export const zDocumentAssociation = z.object({
    type: z
        .enum([
            'tour',
            'tour-stop',
            'service-order',
            'transport-order',
            'problem',
            'handling-unit',
            'bulk-material-unit',
        ])
        .optional(),
    id: zIdString.optional(),
});

/**
 * A document pertaining to (some part of) this `Tour` via association
 */
export const zDocument = zWriteDocument.and(
    z.object({
        associations: z.array(zDocumentAssociation).optional(),
    })
);

export const zSignatureCapture = z.object({
    document: zDocument,
    taken_at: zTimestamp,
    taken_at_position: zCoordinates.optional(),
});

export const zTransportOrderConfirmation = z.object({
    driver_name: z.string().optional(),
    customer_name: z.string().optional(),
    customer_email: z.string().optional(),
    triggered_at: zTimestamp,
    triggered_at_position: zCoordinates.optional(),
    stop_id: zIdString,
    driver_signature: zSignatureCapture.optional(),
    customer_signature: zSignatureCapture.optional(),
});

export const zDocuments = z.array(zDocument);

/**
 * A stop that is executed as part of a tour.
 */
export const zTourStop = z
    .object({
        id: zIdString,
        external_id: z.string().optional(),
        planned_arrival_at: zTimestamp,
        planned_departure_at: zTimestamp,
        location: zLocation,
        additional_customer_data: zAdditionalCustomerData.optional(),
        load: z.array(zIdString).optional(),
        unload: z.array(zIdString).optional(),
        execute: z.array(zIdString).optional(),
        documents: zDocuments.optional(),
        reached_at: zTimestamp.optional(),
        reached_at_mileage_state: zMileageState.optional(),
        unloading_started_at: zTimestamp.optional(),
        unloading_finished_at: zTimestamp.optional(),
        loading_started_at: zTimestamp.optional(),
        loading_finished_at: zTimestamp.optional(),
        execution_started_at: zTimestamp.optional(),
        execution_finished_at: zTimestamp.optional(),
        finished_at: zTimestamp.optional(),
        added_at: zTimestamp.optional(),
        cancelled_at: zTimestamp.optional(),
        skipped_at: zTimestamp.optional(),
    })
    .readonly();

export const zServiceOrder = z
    .object({
        id: zIdString,
        external_id: z.string().optional(),
        order: z.string(),
        category: z.string().nullish(),
        company: z.string().nullish(),
        note: z.string().optional(),
        secondary_reference_id: z.string().optional(),
        additional_customer_data: zAdditionalCustomerData.optional(),
        documents: zDocuments.optional(),
        started_at: zTimestamp.optional(),
        finished_at: zTimestamp.optional(),
        added_at: zTimestamp.optional(),
        cancelled_at: zTimestamp.optional(),
    })
    .readonly();

/**
 * Bulk material
 *
 */
export const zBulkMaterialUnit = z
    .object({
        id: zIdString,
        quantity: zMaterialQuantity,
        material: zMaterial,
        documents: zDocuments.optional(),
        reported_at: zTimestamp.optional(),
        proof_of_loading: zBulkMaterialUnitProofOfHandling.optional(),
        proof_of_delivery: zBulkMaterialUnitProofOfHandling.optional(),
    })
    .readonly();

export const zBulkMaterial = zGoodsBase.and(
    z.object({
        type: z.enum(['bulk-material']).optional(),
        units: z.array(zBulkMaterialUnit),
    })
);

/**
 * A HandlingUnit is a unit of goods that combines the characteristics of load units and packages. On `CargoMaterial` level it represents a load unit where the `packaging_type` could be e.g. euro-pallet.
 * If the `HandlingUnit` is part of another `HandlingUnits` content it represents a package where the `packaging_code` could be one of packaging materials like a plastic container or a carton of a specific size.
 *
 */
export const zHandlingUnit = z
    .object({
        id: zIdString,
        packaging_type: z.string(),
        quantity: z
            .int()
            .min(-2147483648, { error: 'Invalid value: Expected int32 to be >= -2147483648' })
            .max(2147483647, { error: 'Invalid value: Expected int32 to be <= 2147483647' }),
        stacking_factor: z
            .int()
            .min(-2147483648, { error: 'Invalid value: Expected int32 to be >= -2147483648' })
            .max(2147483647, { error: 'Invalid value: Expected int32 to be <= 2147483647' })
            .optional(),
        dimension: zDimension.optional(),
        weight: zWeight.optional(),
        loading_meter: zLoadingMeter.optional(),
        articles: z.array(zArticle).optional(),
        documents: zDocuments.optional(),
        reported_at: zTimestamp.optional(),
        proof_of_loading: zProofOfHandling.optional(),
        proof_of_delivery: zProofOfHandling.optional(),
    })
    .readonly();

export const zCargoMaterial = zGoodsBase.and(
    z.object({
        type: z.enum(['cargo-material']).optional(),
        handling_units: z.array(zHandlingUnit),
    })
);

/**
 * The goods being transported in the transport order.
 *
 */
export const zGoods = z.union([
    z
        .object({
            type: z.literal('cargo-material'),
        })
        .and(zCargoMaterial),
    z
        .object({
            type: z.literal('customer-trailer'),
        })
        .and(zCustomerTrailer),
    z
        .object({
            type: z.literal('swap-body'),
        })
        .and(zSwapBody),
    z
        .object({
            type: z.literal('bulk-material'),
        })
        .and(zBulkMaterial),
]);

export const zTransportOrder = z
    .object({
        id: zIdString,
        external_id: z.string().optional(),
        reference_id: z.string().optional(),
        secondary_reference_id: z.string().optional(),
        additional_customer_data: zAdditionalCustomerData.optional(),
        contact: zContact.optional(),
        loading: zLoading.optional(),
        unloading: zUnloading.optional(),
        goods: zGoods.optional(),
        documents: zDocuments.optional(),
        loaded_at: zTimestamp.optional(),
        delivered_at: zTimestamp.optional(),
        added_at: zTimestamp.optional(),
        cancelled_at: zTimestamp.optional(),
        loading_confirmation: zTransportOrderConfirmation.optional(),
        unloading_confirmation: zTransportOrderConfirmation.optional(),
    })
    .readonly();

/**
 * Both the plan data and execution data of a `Tour`.
 */
export const zTour = z
    .object({
        id: zIdString,
        external_id: z.string().optional(),
        name: z.string().optional(),
        account_id: zIdString,
        note: z.string().optional(),
        trailer: zTrailer.optional(),
        drivers: z.array(z.string()),
        additional_customer_data: zAdditionalCustomerData.optional(),
        status: zTourStatus,
        service_orders: z.array(zServiceOrder).optional(),
        transport_orders: z.array(zTransportOrder).optional(),
        means_of_transport: zMeansOfTransport.optional(),
        stops: z.array(zTourStop),
        problems: zTourProblems.optional(),
        documents: zDocuments.optional(),
        seen_at: zTimestamp.optional(),
        started_at: zTimestamp.optional(),
        started_at_mileage_state: zMileageState.optional(),
        finished_at: zTimestamp.optional(),
        finished_at_mileage_state: zMileageState.optional(),
        etag: z.string(),
    })
    .readonly();

/**
 * A list of `Tour`s with pagination data.
 */
export const zTourCursor = z.object({
    items: z.array(zTour).optional(),
    next: z.string().optional(),
});

export const zDocumentList = z.object({
    items: zDocuments.optional(),
});

/**
 * Upload or download link for the given file name
 */
export const zFileLink = z.object({
    file_name: z.string(),
    action_link: z.string(),
});

export const zLink = z.object({
    href: z.string(),
});

export const zSchemaProblem = z.object({
    type: z.url().optional().default('about:blank'),
    title: z.string().optional(),
    status: z.int().gte(100).lt(600).optional(),
    detail: z.string().optional(),
    instance: z.url().optional(),
});

export const zProblem = zSchemaProblem.and(
    z.object({
        code: z.string().optional(),
    })
);

/**
 * An ISO 8601 formatted timestamp.
 */
export const zTimestampWritable = z.string();

export const zCustomerTrailerProofOfHandlingWritable = z
    .object({
        confirmed_at: zTimestampWritable,
        license_plate: z.string().optional(),
    })
    .readonly();

/**
 * A trailer that can be assigned as goods for a transport order.
 */
export const zTrailerUnitWritable = z
    .object({
        id: z.string(),
        license_plate: z.string().optional(),
        note: z.string().optional(),
        proof_of_loading: zCustomerTrailerProofOfHandlingWritable.optional(),
        proof_of_delivery: zCustomerTrailerProofOfHandlingWritable.optional(),
    })
    .readonly();

/**
 * The trailers that are to be delivered in the transport order.
 */
export const zCustomerTrailerWritable = zGoodsBase.and(
    z.object({
        type: z.enum(['customer-trailer']).optional(),
        trailers: z.array(zTrailerUnitWritable),
    })
);

export const zSwapBodyProofOfHandlingWritable = z
    .object({
        confirmed_at: zTimestampWritable,
        swap_body_reference: z.string().optional(),
    })
    .readonly();

export const zSwapBodyUnitWritable = z
    .object({
        id: z.string(),
        proof_of_loading: zSwapBodyProofOfHandlingWritable.optional(),
        proof_of_delivery: zSwapBodyProofOfHandlingWritable.optional(),
    })
    .readonly();

/**
 * The swap body used in a transport order.
 */
export const zSwapBodyWritable = zGoodsBase.and(
    z.object({
        type: z.enum(['swap-body']).optional(),
        units: z.array(zSwapBodyUnitWritable),
    })
);

export const zOrderHandleWritable = zIdString;

export const zTourProblemWritable = z
    .object({
        id: zIdString,
        type: zTourProblemType,
        tour_stop_id: zIdString.optional(),
        documents: zWriteDocuments.optional(),
        reported_at: zTimestampWritable.optional(),
        acknowledged_at: zTimestampWritable.optional(),
    })
    .readonly();

export const zTourProblemsWritable = z.array(zTourProblemWritable);

export const zProblemReportedEventWritable = z.object({
    id: z.uuid(),
    account_id: z.uuid(),
    tour_id: z.string(),
    occurred_at: z.string(),
    event_type: z.enum(['problem-reported']),
    problem: zTourProblemWritable,
    triggered_at_position: zCoordinates.optional(),
    mileage_state: zMileageState.optional(),
});

export const zBaseTourEventWritable = z.union([
    zTourEvent,
    zTourStopEvent,
    zProblemReportedEventWritable,
    zProblemAcknowledgedEvent,
    zCustomerTrailerConfirmedEvent,
    zSwapBodyConfirmedEvent,
    zHandlingUnitEvent,
    zBulkMaterialUnitConfirmedEvent,
    zTransportOrderConfirmedEvent,
]);

/**
 * A document pertaining to (some part of) this `Tour` via association
 */
export const zDocumentWritable = zWriteDocument.and(
    z.object({
        associations: z.array(zDocumentAssociation).optional(),
    })
);

export const zSignatureCaptureWritable = z.object({
    document: zDocumentWritable,
    taken_at: zTimestampWritable,
    taken_at_position: zCoordinates.optional(),
});

export const zTransportOrderConfirmationWritable = z.object({
    driver_name: z.string().optional(),
    customer_name: z.string().optional(),
    customer_email: z.string().optional(),
    triggered_at: zTimestampWritable,
    triggered_at_position: zCoordinates.optional(),
    stop_id: zIdString,
    driver_signature: zSignatureCaptureWritable.optional(),
    customer_signature: zSignatureCaptureWritable.optional(),
});

export const zDocumentsWritable = z.array(zDocumentWritable);

/**
 * A stop that is executed as part of a tour.
 */
export const zTourStopWritable = z
    .object({
        id: zIdString,
        external_id: z.string().optional(),
        planned_arrival_at: zTimestampWritable,
        planned_departure_at: zTimestampWritable,
        location: zLocation,
        additional_customer_data: zAdditionalCustomerData.optional(),
        load: z.array(zIdString).optional(),
        unload: z.array(zIdString).optional(),
        execute: z.array(zIdString).optional(),
        documents: zDocumentsWritable.optional(),
        reached_at: zTimestampWritable.optional(),
        reached_at_mileage_state: zMileageState.optional(),
        unloading_started_at: zTimestampWritable.optional(),
        unloading_finished_at: zTimestampWritable.optional(),
        loading_started_at: zTimestampWritable.optional(),
        loading_finished_at: zTimestampWritable.optional(),
        execution_started_at: zTimestampWritable.optional(),
        execution_finished_at: zTimestampWritable.optional(),
        finished_at: zTimestampWritable.optional(),
        added_at: zTimestampWritable.optional(),
        cancelled_at: zTimestampWritable.optional(),
        skipped_at: zTimestampWritable.optional(),
    })
    .readonly();

export const zServiceOrderWritable = z
    .object({
        id: zIdString,
        external_id: z.string().optional(),
        order: z.string(),
        category: z.string().nullish(),
        company: z.string().nullish(),
        note: z.string().optional(),
        secondary_reference_id: z.string().optional(),
        additional_customer_data: zAdditionalCustomerData.optional(),
        documents: zDocumentsWritable.optional(),
        started_at: zTimestampWritable.optional(),
        finished_at: zTimestampWritable.optional(),
        added_at: zTimestampWritable.optional(),
        cancelled_at: zTimestampWritable.optional(),
    })
    .readonly();

/**
 * Bulk material
 *
 */
export const zBulkMaterialUnitWritable = z
    .object({
        id: zIdString,
        quantity: zMaterialQuantity,
        material: zMaterial,
        documents: zDocumentsWritable.optional(),
        reported_at: zTimestampWritable.optional(),
        proof_of_loading: zBulkMaterialUnitProofOfHandling.optional(),
        proof_of_delivery: zBulkMaterialUnitProofOfHandling.optional(),
    })
    .readonly();

export const zBulkMaterialWritable = zGoodsBase.and(
    z.object({
        type: z.enum(['bulk-material']).optional(),
        units: z.array(zBulkMaterialUnitWritable),
    })
);

/**
 * A HandlingUnit is a unit of goods that combines the characteristics of load units and packages. On `CargoMaterial` level it represents a load unit where the `packaging_type` could be e.g. euro-pallet.
 * If the `HandlingUnit` is part of another `HandlingUnits` content it represents a package where the `packaging_code` could be one of packaging materials like a plastic container or a carton of a specific size.
 *
 */
export const zHandlingUnitWritable = z
    .object({
        id: zIdString,
        packaging_type: z.string(),
        quantity: z
            .int()
            .min(-2147483648, { error: 'Invalid value: Expected int32 to be >= -2147483648' })
            .max(2147483647, { error: 'Invalid value: Expected int32 to be <= 2147483647' }),
        stacking_factor: z
            .int()
            .min(-2147483648, { error: 'Invalid value: Expected int32 to be >= -2147483648' })
            .max(2147483647, { error: 'Invalid value: Expected int32 to be <= 2147483647' })
            .optional(),
        dimension: zDimension.optional(),
        weight: zWeight.optional(),
        loading_meter: zLoadingMeter.optional(),
        articles: z.array(zArticle).optional(),
        documents: zDocumentsWritable.optional(),
        reported_at: zTimestampWritable.optional(),
        proof_of_loading: zProofOfHandling.optional(),
        proof_of_delivery: zProofOfHandling.optional(),
    })
    .readonly();

export const zCargoMaterialWritable = zGoodsBase.and(
    z.object({
        type: z.enum(['cargo-material']).optional(),
        handling_units: z.array(zHandlingUnitWritable),
    })
);

/**
 * The goods being transported in the transport order.
 *
 */
export const zGoodsWritable = z.union([
    z
        .object({
            type: z.literal('cargo-material'),
        })
        .and(zCargoMaterialWritable),
    z
        .object({
            type: z.literal('customer-trailer'),
        })
        .and(zCustomerTrailerWritable),
    z
        .object({
            type: z.literal('swap-body'),
        })
        .and(zSwapBodyWritable),
    z
        .object({
            type: z.literal('bulk-material'),
        })
        .and(zBulkMaterialWritable),
]);

export const zTransportOrderWritable = z
    .object({
        id: zIdString,
        external_id: z.string().optional(),
        reference_id: z.string().optional(),
        secondary_reference_id: z.string().optional(),
        additional_customer_data: zAdditionalCustomerData.optional(),
        contact: zContact.optional(),
        loading: zLoading.optional(),
        unloading: zUnloading.optional(),
        goods: zGoodsWritable.optional(),
        documents: zDocumentsWritable.optional(),
        loaded_at: zTimestampWritable.optional(),
        delivered_at: zTimestampWritable.optional(),
        added_at: zTimestampWritable.optional(),
        cancelled_at: zTimestampWritable.optional(),
        loading_confirmation: zTransportOrderConfirmationWritable.optional(),
        unloading_confirmation: zTransportOrderConfirmationWritable.optional(),
    })
    .readonly();

/**
 * Both the plan data and execution data of a `Tour`.
 */
export const zTourWritable = z
    .object({
        id: zIdString,
        external_id: z.string().optional(),
        name: z.string().optional(),
        account_id: zIdString,
        note: z.string().optional(),
        trailer: zTrailer.optional(),
        drivers: z.array(z.string()),
        additional_customer_data: zAdditionalCustomerData.optional(),
        status: zTourStatus,
        service_orders: z.array(zServiceOrderWritable).optional(),
        transport_orders: z.array(zTransportOrderWritable).optional(),
        means_of_transport: zMeansOfTransport.optional(),
        stops: z.array(zTourStopWritable),
        problems: zTourProblemsWritable.optional(),
        documents: zDocumentsWritable.optional(),
        seen_at: zTimestampWritable.optional(),
        started_at: zTimestampWritable.optional(),
        started_at_mileage_state: zMileageState.optional(),
        finished_at: zTimestampWritable.optional(),
        finished_at_mileage_state: zMileageState.optional(),
        etag: z.string(),
    })
    .readonly();

/**
 * A list of `Tour`s with pagination data.
 */
export const zTourCursorWritable = z.object({
    items: z.array(zTourWritable).optional(),
    next: z.string().optional(),
});

export const zDocumentListWritable = z.object({
    items: zDocumentsWritable.optional(),
});

/**
 * The asset id of the `MeansOfTransport` for which to retrieve data.
 */
export const zAssetId2 = zIdString;

/**
 * The asset id of the `MeansOfTransport` for which to retrieve the active tour.
 */
export const zAssetIdRequired = zIdString;

/**
 * Limit the number of returned resources in list
 */
export const zLimit = z.int();

/**
 * A cursor for pagination. Is either an identifier or a composite identifier, base64 encoded.
 */
export const zCursor = z.string();
